import React, { useCallback, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Check, ChevronRight, RefreshCw, Send, Sparkles } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { COLORS, MARKS } from './fields';

/**
 * KuusiKenttaaFields — the task UI shared by its two doors (kuusi_kenttaa.v1,
 * specs/workshop-modules/kuusi_kenttaa.md): the /me pre-work task
 * (KuusiKenttaaTask) and the room page behind the workshop PIN
 * (KuusiKenttaaRoomScreen). The doors load the data and save; this renders.
 *
 * A team picker until `data.project` is set, then six cards, one per field:
 * what the project's documents say (verdict mark, quote, source) as the
 * prompt, a text field for the team's answer, and an "emme tiedä" toggle.
 * Each answer saves on blur; the toggle saves at once.
 *
 * Under an answered field the AI's one follow-up question (kuusi_kenttaa_followups):
 * pending while it is being asked, then the question with a reply box, Vastaa /
 * Ohita; answered and skipped stay visible. A changed answer brings a new question.
 *
 *   data        { projects, project, items } from either task route; items carry `followup`
 *   onPick      (slug) => void — the team chosen
 *   onUnpick    () => void — back to the picker
 *   putAnswer   ({ project, field, text, dontKnow }) => Promise<answer>
 *   putFollowup ({ project, field, text, skip }) => Promise<followup>
 *   onSaved     () => void — an answer was saved: the door reloads, so the pending question shows
 */

const Column = styled.div`display: flex; flex-direction: column; gap: 0.9rem; min-width: 0;`;

const Intro = styled.p`margin: 0; color: ${COLORS.muted}; line-height: 1.5;`;

const Card = styled.div`
  background: ${COLORS.card};
  border: 1px solid ${COLORS.border};
  border-radius: 10px;
  padding: 1rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  min-width: 0;
`;

const Head = styled.div`display: flex; align-items: baseline; gap: 0.6rem; flex-wrap: wrap;`;

const Mark = styled.span`
  font-size: 1.1rem;
  line-height: 1;
  color: ${(p) => (p.$state === 'filled' ? COLORS.filled : p.$state === 'suggested' ? COLORS.suggested : COLORS.dim)};
`;

const Label = styled.strong`font-size: 1.02rem;`;

const Question = styled.div`color: ${COLORS.text}; line-height: 1.45;`;

const Prompt = styled.blockquote`
  margin: 0;
  padding: 0.5rem 0 0.5rem 0.8rem;
  border-left: 3px solid ${(p) => (p.$verdict === 'present' ? COLORS.filled : p.$verdict === 'inferable' ? COLORS.suggested : COLORS.border)};
  color: ${COLORS.muted};
  font-size: 0.95rem;
  line-height: 1.5;
  overflow-wrap: anywhere;
`;

const Source = styled.div`color: ${COLORS.dim}; font-size: 0.82rem;`;

const Verdict = styled.span`
  font-size: 0.75rem;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${(p) => (p.$verdict === 'present' ? COLORS.filled : p.$verdict === 'inferable' ? COLORS.suggested : COLORS.dim)};
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 4.2rem;
  resize: vertical;
  background: #0f1219;
  color: ${COLORS.text};
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  padding: 0.6rem 0.7rem;
  font: inherit;
  line-height: 1.45;
  &:focus { outline: none; border-color: ${COLORS.borderHover}; }
  &:disabled { opacity: 0.5; }
`;

const Row = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 0.6rem; flex-wrap: wrap;`;

const Toggle = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.75rem;
  border-radius: 999px;
  font-size: 0.9rem;
  border: 1px solid ${(p) => (p.$on ? COLORS.suggested : COLORS.border)};
  color: ${(p) => (p.$on ? COLORS.suggested : COLORS.muted)};
  &:hover, &:focus-visible { border-color: ${COLORS.borderHover}; }
`;

const Status = styled.span`
  font-size: 0.82rem;
  color: ${(p) => (p.$tone === 'error' ? COLORS.error : p.$tone === 'saved' ? COLORS.filled : COLORS.dim)};
`;

const ProjectButton = styled.button`
  all: unset;
  cursor: pointer;
  background: ${COLORS.card};
  border: 1px solid ${COLORS.border};
  border-radius: 10px;
  padding: 0.9rem 1.1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  &:hover, &:focus-visible { border-color: ${COLORS.borderHover}; }
`;

const Small = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: ${COLORS.muted};
  font-size: 0.85rem;
  &:hover, &:focus-visible { color: ${COLORS.text}; }
`;

const Follow = styled.div`
  margin-top: 0.2rem;
  padding: 0.7rem 0.8rem;
  border-radius: 8px;
  background: rgba(124, 58, 237, 0.08);
  border: 1px dashed rgba(167, 139, 250, 0.45);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
const FollowHead = styled.div`display: flex; align-items: center; gap: 0.4rem; color: #c4b5fd; font-size: 0.8rem; letter-spacing: 0.03em; text-transform: uppercase;`;
const FollowQ = styled.div`color: ${COLORS.text}; line-height: 1.45; font-weight: 600;`;
const FollowMeta = styled.div`color: ${COLORS.dim}; font-size: 0.8rem;`;
/** The AI is reading the answer: a pulsing dot and the line, so the minute it takes is visibly a wait, not a silence. */
const pulse = keyframes`0%, 100% { opacity: 0.25; } 50% { opacity: 1; }`;
const Waiting = styled.div`
  display: flex; align-items: center; gap: 0.5rem;
  color: #c4b5fd; font-size: 0.85rem;
  &::before { content: ''; width: 0.55rem; height: 0.55rem; border-radius: 50%; background: #a78bfa; animation: ${pulse} 1.2s ease-in-out infinite; flex: 0 0 auto; }
`;
const FollowA = styled.div`color: ${COLORS.muted}; line-height: 1.45; white-space: pre-wrap;`;
const Act = styled.button`
  all: unset;
  cursor: pointer;
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
  font-size: 0.9rem;
  border: 1px solid ${(p) => (p.$primary ? 'rgba(167,139,250,0.7)' : COLORS.border)};
  background: ${(p) => (p.$primary ? 'rgba(124,58,237,0.3)' : 'transparent')};
  color: ${(p) => (p.$primary ? '#e9d5ff' : COLORS.muted)};
  &:hover, &:focus-visible { border-color: ${COLORS.borderHover}; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

export const Err = styled.div`color: ${COLORS.error}; line-height: 1.45;`;
export { Intro };

const stateOf = (item, draft) => {
  if (draft && !draft.dontKnow && draft.text.trim()) return 'filled';
  const v = item.suggestion?.verdict;
  return v === 'present' || v === 'inferable' ? 'suggested' : 'open';
};

/** The AI's follow-up under an answered field, in whichever state it is. */
function FollowupBlock({ project, field, followup, locked, putFollowup }) {
  const { t } = useTranslation();
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!followup) return null;
  const send = async (skip) => {
    if (!putFollowup) return;
    setBusy(true); setError(null);
    try { await putFollowup({ project, field, text: skip ? '' : reply, skip }); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const meta = [followup.stage && t(`kuusiKenttaa.followup.stage.${followup.stage}`), followup.target && t('kuusiKenttaa.followup.target', { target: followup.target })].filter(Boolean).join(' · ');
  return (
    <Follow>
      <FollowHead><Sparkles size={13} /> {t('kuusiKenttaa.followup.title')}</FollowHead>
      {followup.status === 'pending' && <Waiting role="status">{t('kuusiKenttaa.followup.pending')}</Waiting>}
      {followup.status === 'failed' && <FollowMeta>{t('kuusiKenttaa.followup.failed')}</FollowMeta>}
      {followup.question && <FollowQ>{followup.question}</FollowQ>}
      {followup.question && meta && <FollowMeta>{meta}</FollowMeta>}
      {followup.status === 'open' && (
        <>
          <TextArea value={reply} placeholder={t('kuusiKenttaa.followup.answerPlaceholder')} disabled={locked || busy} onChange={(e) => setReply(e.target.value)} aria-label={t('kuusiKenttaa.followup.title')} />
          <Row>
            <span>
              <Act type="button" $primary disabled={locked || busy || !reply.trim()} onClick={() => send(false)}>{t('kuusiKenttaa.followup.answer')}</Act>
              {' '}
              <Act type="button" disabled={locked || busy} onClick={() => send(true)}>{t('kuusiKenttaa.followup.skip')}</Act>
            </span>
            {busy && <Status>{t('kuusiKenttaa.saving')}</Status>}
            {error && <Status $tone="error">{t('kuusiKenttaa.saveError', { message: error })}</Status>}
          </Row>
        </>
      )}
      {followup.status === 'answered' && <FollowA>{followup.answerText}</FollowA>}
      {followup.status === 'skipped' && <FollowMeta>{t('kuusiKenttaa.followup.skipped')}</FollowMeta>}
    </Follow>
  );
}

function FieldCard({ project, item, locked, putAnswer, putFollowup, onSaved }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState({ text: item.answer?.text || '', dontKnow: !!item.answer?.dontKnow });
  const [status, setStatus] = useState(null); // null | 'saving' | 'saved' | { error }
  const lastSaved = useRef(JSON.stringify(draft));

  const save = useCallback(async (next) => {
    const key = JSON.stringify(next);
    if (key === lastSaved.current) return;
    setStatus('saving');
    try {
      await putAnswer({ project, field: item.field, text: next.text, dontKnow: next.dontKnow });
      lastSaved.current = key;
      setStatus('saved');
      onSaved?.();
    } catch (err) { setStatus({ error: err.message }); }
  }, [project, item.field, putAnswer, onSaved]);

  const toggleDontKnow = () => {
    const next = { text: draft.dontKnow ? draft.text : '', dontKnow: !draft.dontKnow };
    setDraft(next);
    save(next);
  };

  // Sent = the draft is what the server has. Waiting = sent, and the follow-up
  // has not come back yet (the door's reload brings it as pending, then open).
  const dirty = JSON.stringify(draft) !== lastSaved.current;
  const waitingForFollowup = status === 'saved' && !draft.dontKnow && draft.text.trim() && !item.followup;

  const s = item.suggestion;
  const promptKey = s ? s.verdict : 'none';
  const state = stateOf(item, draft);
  const f = `kuusiKenttaa.fields.${item.field}`;
  return (
    <Card>
      <Head>
        <Mark $state={state} aria-hidden="true">{MARKS[state]}</Mark>
        <Label>{t(`${f}.label`)}</Label>
        <Verdict $verdict={promptKey}>{t(`kuusiKenttaa.verdict.${promptKey}`)}</Verdict>
      </Head>
      <Prompt $verdict={promptKey}>
        {s && s.quote && s.verdict !== 'absent' ? t(`kuusiKenttaa.prompt.${s.verdict}`, { quote: s.quote }) : t(`kuusiKenttaa.prompt.${promptKey}`)}
        {s?.sourceTitle && s.verdict !== 'absent' && <Source>{t('kuusiKenttaa.source', { title: s.sourceTitle })}</Source>}
      </Prompt>
      <Question>{t(`${f}.question`)}</Question>
      <TextArea
        value={draft.text}
        placeholder={t('kuusiKenttaa.answerPlaceholder')}
        disabled={locked || draft.dontKnow}
        onChange={(e) => setDraft((d) => ({ ...d, text: e.target.value }))}
        aria-label={t(`${f}.label`)}
      />
      <Row>
        <Toggle type="button" $on={draft.dontKnow} onClick={toggleDontKnow} disabled={locked} aria-pressed={draft.dontKnow}>
          {draft.dontKnow && <Check size={14} />} {t('kuusiKenttaa.dontKnow')}
        </Toggle>
        {/* An answer goes when the person sends it, not when the field loses focus: a sent answer is a visible act, and the wait that follows has a start (26.10.2026). */}
        <Act type="button" $primary disabled={locked || draft.dontKnow || status === 'saving' || !dirty || !draft.text.trim()} onClick={() => save(draft)}>
          <Send size={14} style={{ verticalAlign: '-2px', marginRight: '0.35rem' }} />
          {status === 'saving' ? t('kuusiKenttaa.saving') : !dirty && status === 'saved' ? t('kuusiKenttaa.sent') : t('kuusiKenttaa.send')}
        </Act>
        {status && status.error && <Status $tone="error">{t('kuusiKenttaa.saveError', { message: status.error })}</Status>}
      </Row>
      {waitingForFollowup && <Waiting role="status">{t('kuusiKenttaa.followup.pending')}</Waiting>}
      <FollowupBlock project={project} field={item.field} followup={item.followup} locked={locked} putFollowup={putFollowup} />
    </Card>
  );
}

export default function KuusiKenttaaFields({ data, locked = false, onPick, onUnpick, putAnswer, putFollowup = null, onSaved = null }) {
  const { t } = useTranslation();

  if (!data.project) {
    return (
      <Column>
        <Intro>{t('kuusiKenttaa.intro')}</Intro>
        <strong>{t('kuusiKenttaa.pickProject')}</strong>
        {data.projects.length === 0 && <Intro>{t('kuusiKenttaa.noProjects')}</Intro>}
        {data.projects.map((p) => (
          <ProjectButton key={p.slug} type="button" onClick={() => onPick(p.slug)}>
            <span>{p.name}</span><ChevronRight size={18} />
          </ProjectButton>
        ))}
        <Source>{t('kuusiKenttaa.pickHint')}</Source>
      </Column>
    );
  }

  return (
    <Column>
      <Row>
        <strong>{data.project.name}</strong>
        <Small type="button" onClick={onUnpick}><RefreshCw size={13} /> {t('kuusiKenttaa.changeProject')}</Small>
      </Row>
      <Intro>{t('kuusiKenttaa.intro')}</Intro>
      {data.items.map((item) => (
        <FieldCard key={item.field} project={data.project.slug} item={item} locked={locked} putAnswer={putAnswer} putFollowup={putFollowup} onSaved={onSaved} />
      ))}
    </Column>
  );
}
